import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["method", "status", "statusContainer", "submit", "startDate", "endDate"]

  connect() {
    console.log("Payment controller connected")
    if (this.hasMethodTarget) {
      // Check initial payment method and toggle status visibility
      this.togglePaymentMethod({ target: this.methodTarget })
    }
    
    // Debug date fields if they exist
    this.debugDateFields()
    
    // Add listeners to date fields if they exist
    this.setupDateListeners()
  }
  
  debugDateFields() {
    // Find date inputs even if they're not direct targets
    const startDateField = document.querySelector('input[name="reservation[start_date]"]')
    const endDateField = document.querySelector('input[name="reservation[end_date]"]')
    
    if (startDateField) {
      console.log("Start date field found:", {
        id: startDateField.id,
        name: startDateField.name,
        value: startDateField.value,
        type: startDateField.type,
        format: startDateField.dataset.format || 'No format specified'
      })
    } else {
      console.log("Start date field not found in the form")
    }
    
    if (endDateField) {
      console.log("End date field found:", {
        id: endDateField.id,
        name: endDateField.name,
        value: endDateField.value,
        type: endDateField.type,
        format: endDateField.dataset.format || 'No format specified'
      })
    } else {
      console.log("End date field not found in the form")
    }
  }
  
  setupDateListeners() {
    // Find date inputs
    const startDateField = document.querySelector('input[name="reservation[start_date]"]')
    const endDateField = document.querySelector('input[name="reservation[end_date]"]')
    
    if (startDateField) {
      startDateField.addEventListener('change', (e) => {
        console.log("Start date changed:", {
          value: e.target.value,
          rawValue: e.target.value,
          dateObject: e.target.value ? new Date(e.target.value) : null
        })
      })
    }
    
    if (endDateField) {
      endDateField.addEventListener('change', (e) => {
        console.log("End date changed:", {
          value: e.target.value,
          rawValue: e.target.value,
          dateObject: e.target.value ? new Date(e.target.value) : null
        })
      })
    }
  }

  togglePaymentMethod(event) {
    try {
      const method = event.target.value
      console.log("Payment method changed:", method)
      
      // Check if targets exist before trying to access them
      if (!this.hasStatusTarget || !this.hasStatusContainerTarget) {
        console.log("Status targets not found - this is expected during page transitions")
        return // Exit early if targets don't exist
      }
      
      console.log("Status targets found:", {
        status: this.statusTarget,
        container: this.statusContainerTarget
      })
      
      if (method === 'credit_card') {
        console.log("Credit card selected, hiding status field")
        // For credit card, hide the status field completely
        this.statusContainerTarget.classList.add('hidden')
        console.log("Added 'hidden' class to status container")
        
        // Set status to pending_payment
        this.statusTarget.value = 'pending_payment'
        this.statusTarget.disabled = true
        console.log("Status set to pending_payment and disabled")
      } else {
        console.log("Non-credit card payment method selected, showing status field")
        // For cash/check, show the status field
        this.statusContainerTarget.classList.remove('hidden')
        console.log("Removed 'hidden' class from status container")
      
        // Allow status selection
        this.statusTarget.disabled = false
        console.log("Status field enabled")
      }
    } catch (error) {
      console.error("Error in togglePaymentMethod:", error)
    }
  }

  async handleSubmit(event) {
    event.preventDefault()
    const form = event.target.closest('form')
    console.log("payment #handleSubmit")
    console.log("Form details:", { 
      action: form.action,
      method: form.method,
      enctype: form.enctype
    })
    
    // Validate dates using datepicker controller if available
    this.validateDatesBeforeSubmission(form)
    
    // Debug form data
    const formData = new FormData(form)
    const formDataObj = {}
    formData.forEach((value, key) => {
      formDataObj[key] = value
    })
    console.log("Form data:", formDataObj)
    
    // Detailed date debugging at submission time
    const startDate = formData.get('reservation[start_date]')
    const endDate = formData.get('reservation[end_date]')
    
    console.log("DATES AT SUBMISSION:", {
      startDate: {
        value: startDate,
        type: typeof startDate,
        jsDate: startDate ? new Date(startDate) : null,
        jsDateISO: startDate ? new Date(startDate).toISOString() : null,
        jsDateLocale: startDate ? new Date(startDate).toLocaleDateString() : null
      },
      endDate: {
        value: endDate,
        type: typeof endDate,
        jsDate: endDate ? new Date(endDate) : null,
        jsDateISO: endDate ? new Date(endDate).toISOString() : null,
        jsDateLocale: endDate ? new Date(endDate).toLocaleDateString() : null
      }
    })
    
    const paymentMethod = form.querySelector('[name="reservation[payment_method]"]')?.value
    console.log("Payment method:", paymentMethod)

    if (paymentMethod !== 'credit_card') {
      console.log("Non-credit card payment, submitting normally")
      form.submit()
      return
    }

    // For credit card payments, handle with Stripe
    console.log("Credit card payment, handling with Stripe")

    // Show loading state
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.innerHTML = `
        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Processing...
      `
    }

    try {
      await this.createCheckoutSession(form)
    } catch (error) {
      console.error('Error in handleSubmit:', error)
      this.showError(error.message || 'An unexpected error occurred')
      
      // Reset submit button
      if (this.hasSubmitTarget) {
        this.submitTarget.disabled = false
        this.submitTarget.innerHTML = 'Create Reservation'
      }
    }
  }
  
  // Manual validation. The optimistic datepicker Stimulus-lookup path was
  // never reachable — no ERB ever set data-controller="datepicker".
  validateDatesBeforeSubmission(form) {
    const startDateField = form.querySelector('input[name="reservation[start_date]"]')
    const endDateField = form.querySelector('input[name="reservation[end_date]"]')

    if (startDateField && endDateField) {
      try {
        const startDate = new Date(startDateField.value)
        const endDate = new Date(endDateField.value)

        const formatDate = (date) => {
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          return `${month}/${day}/${year}`
        }

        if (!isNaN(startDate.getTime())) {
          startDateField.value = formatDate(startDate)
        }

        if (!isNaN(endDate.getTime())) {
          endDateField.value = formatDate(endDate)
        }
      } catch (error) {
        console.error("Error validating dates manually:", error)
      }
    }
  }

  async createCheckoutSession(form) {
    console.log("Creating checkout session...")
    const formData = new FormData(form)
    
    // Ensure dates are in MM/DD/YYYY format
    const startDateField = form.querySelector('input[name="reservation[start_date]"]')
    const endDateField = form.querySelector('input[name="reservation[end_date]"]')
    
    if (startDateField && endDateField) {
      try {
        // Parse the dates - explicitly handle MM/DD/YYYY format
        const parseDateMMDDYYYY = (dateStr) => {
          console.log(`Parsing date string: "${dateStr}"`)
          
          // Split by / and ensure we have 3 parts
          const parts = dateStr.split('/')
          if (parts.length !== 3) {
            throw new Error(`Invalid date format: ${dateStr}. Expected MM/DD/YYYY`)
          }
          
          // Parse parts as numbers
          const month = parseInt(parts[0], 10)
          const day = parseInt(parts[1], 10)
          const year = parseInt(parts[2], 10)
          
          console.log(`Parsed date parts: month=${month}, day=${day}, year=${year}`)
          
          // Validate parts
          if (isNaN(month) || month < 1 || month > 12) {
            throw new Error(`Invalid month: ${month}`)
          }
          if (isNaN(day) || day < 1 || day > 31) {
            throw new Error(`Invalid day: ${day}`)
          }
          if (isNaN(year) || year < 2000 || year > 2100) {
            throw new Error(`Invalid year: ${year}`)
          }
          
          // Create date object (month is 0-indexed in JS Date)
          const date = new Date(year, month - 1, day)
          console.log(`Created date object: ${date.toISOString()}`)
          return date
        }
        
        // Format dates as MM/DD/YYYY
        const formatDateMMDDYYYY = (date) => {
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          const year = date.getFullYear()
          return `${month}/${day}/${year}`
        }
        
        // Parse dates using our explicit parser
        const startDate = parseDateMMDDYYYY(startDateField.value)
        const endDate = parseDateMMDDYYYY(endDateField.value)
        
        // Get today's date at midnight for comparison (to match server-side validation)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        
        console.log("Date validation:", {
          today: today.toISOString(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          startDateBeforeToday: startDate < today,
          endDateBeforeToday: endDate < today,
          endDateBeforeOrEqualStartDate: endDate <= startDate
        })
        
        // Validate dates are not in the past
        if (startDate < today) {
          throw new Error('Check-in date cannot be in the past')
        }
        
        if (endDate < today) {
          throw new Error('Check-out date cannot be in the past')
        }
        
        if (endDate <= startDate) {
          throw new Error('Check-out date must be after check-in date')
        }
        
        // Format dates consistently
        const formattedStartDate = formatDateMMDDYYYY(startDate)
        const formattedEndDate = formatDateMMDDYYYY(endDate)
        
        console.log("Ensuring dates are in MM/DD/YYYY format:", {
          original: {
            startDate: startDateField.value,
            endDate: endDateField.value
          },
          parsed: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          },
          formatted: {
            startDate: formattedStartDate,
            endDate: formattedEndDate
          }
        })
        
        // Update form data with formatted dates
        formData.set('reservation[start_date]', formattedStartDate)
        formData.set('reservation[end_date]', formattedEndDate)
      } catch (error) {
        console.error("Error formatting dates:", error)
        this.showError(error.message)
        throw error
      }
    }
    
    // Debug dates in form data
    const startDate = formData.get('reservation[start_date]')
    const endDate = formData.get('reservation[end_date]')
    console.log("Date values in request:", { 
      startDate, 
      endDate,
      startDateType: typeof startDate,
      endDateType: typeof endDate
    })
    
    // Debug user information
    const userId = formData.get('reservation[user_id]')
    console.log("User ID:", userId)
    
    // Debug CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    console.log("CSRF Token present:", !!csrfToken)

    try {
      console.log("Sending fetch request to:", form.action)
      const response = await fetch(form.action, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: formData
      })

      console.log("Response status:", response.status)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error("Error response:", errorText)
        throw new Error(`Server error: ${response.status}`)
      }

      const data = await response.json()
      console.log("Response data:", data)

      if (data.error) {
        throw new Error(data.error)
      }

      if (data.checkout_url) {
        console.log("Redirecting to Stripe checkout:", data.checkout_url)
        window.location.href = data.checkout_url
      } else {
        throw new Error('No checkout URL returned from server')
      }
    } catch (error) {
      console.error("Fetch error:", error)
      throw error
    }
  }

  showError(message) {
    console.error("Payment error:", message)
    
    // Create an error notification
    const notification = document.createElement('div')
    notification.className = 'fixed inset-0 flex items-end justify-center px-4 py-6 pointer-events-none sm:p-6 sm:items-start sm:justify-end z-50'
    notification.innerHTML = `
      <div class="max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden">
        <div class="p-4">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg class="h-6 w-6 text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="ml-3 w-0 flex-1 pt-0.5">
              <p class="text-sm font-medium text-gray-900">Payment Error</p>
              <p class="mt-1 text-sm text-gray-500">${message}</p>
            </div>
            <div class="ml-4 flex-shrink-0 flex">
              <button class="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                <span class="sr-only">Close</span>
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `
    
    document.body.appendChild(notification)
    
    // Add click event to close button
    const closeButton = notification.querySelector('button')
    closeButton.addEventListener('click', () => {
      notification.remove()
    })
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.remove()
    }, 5000)
  }
}
